import { FactDocumentDetails } from './document-details';
import { RelevanceSelector } from './relevance-selector';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import EditableField from '@/components/molecules/EditableField';
import { renderHighlightedTextWithLinks } from '@/helpers/textRendering';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { Comments } from '@/new/components/fact/comments';
import { Icon } from '@/new/components/icon';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { Fact, Relevance } from '@/types';

interface FactDetailProps {
  fact: Fact;
  significanceText: string;
  relevanceText: Relevance;
  updateRelevance?: (v: Relevance) => void;
  updateSignificance: (v: string) => void;
  onViewDocument?: (docId: string, factId: string, pageNumber: number) => void;
  isUpdatingSignificance: boolean;
  isUpdatingRelevance?: boolean;
  handleOpenDeleteModal: () => void;
}

export const FactDetail = ({
  fact,
  significanceText,
  relevanceText,
  updateRelevance,
  updateSignificance,
  onViewDocument,
  isUpdatingSignificance,
  isUpdatingRelevance,
  handleOpenDeleteModal,
}: FactDetailProps) => {
  const { openDocViewer } = useDocumentNavigation();

  // updateRelevance function only passed in for full-facts, so this works as a proxy (for now)
  const isFullFact = updateRelevance !== undefined;

  return (
    <div>
      <div className="mt-2 border border-x-0 border-t-0 border-dashed border-blue-200" />

      <div className="flex gap-4 py-4 pr-4 pl-2">
        <div className="w-full">
          <div className="mb-1 flex items-center gap-1 px-2 font-semibold text-gray-800">
            Significance
            <TooltipIcon tooltipContent="A short analysis of the significance of the fact as it pertains to the key case issues and the context of the document it is extracted from." />
          </div>
          <div className="px-2 text-gray-800">
            <EditableField value={significanceText} onSave={updateSignificance} isLoading={isUpdatingSignificance} />
          </div>
        </div>

        {updateRelevance && isUpdatingRelevance !== undefined && (
          <div className="w-full max-w-3xs border border-y-0 border-r-0 border-dashed border-blue-200 pl-6">
            <div>
              <div className="mt-2 mb-1 flex items-center gap-1 font-semibold text-gray-800">
                Relevance
                <TooltipIcon tooltipContent="Is this fact relevant to the case issues." />
              </div>
              <RelevanceSelector relevanceText={relevanceText} updateRelevance={updateRelevance} />

              <div className="mt-2 mb-1 flex items-center gap-1 font-semibold text-gray-800">
                Classification
                <TooltipIcon tooltipContent="Fact classification. One of Primary, Secondary, Future or Litigation." />
              </div>
              {fact.classification ? fact.classification.charAt(0).toUpperCase() + fact.classification.slice(1) : ''}
            </div>
          </div>
        )}
      </div>

      <div className="border border-x-0 border-t-0 border-dashed border-blue-200"></div>

      <div className="py-4 pr-4 pl-2">
        <FactDocumentDetails
          fact={fact}
          gotoDocReference={
            onViewDocument
              ? onViewDocument
              : (docId: string, factId: string, pageNum: number) => {
                  openDocViewer(docId, factId, pageNum);
                  trackCustomEvent(TrackingEvents.OpenDocViewerFromFact);
                }
          }
        />
      </div>

      <div className="flex">
        <div className="flex flex-1 justify-between gap-10 rounded-b-lg border-t-2 border-blue-200 bg-blue-50 px-4">
          <div>
            <div className="pt-3 pb-1 font-semibold">Comments</div>
            <div>
              {fact.comments?.length > 0 ? (
                renderHighlightedTextWithLinks(fact.comments[0].content)
              ) : (
                <span className="font-light text-gray-500">No comments yet</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 py-3">
            <Comments
              matterId={fact.caseId}
              factId={fact.id}
              commentsCount={fact.comments?.length || 0}
              comments={fact.comments}
            />
            <div className="flex h-full items-center">
              <button
                onClick={handleOpenDeleteModal}
                className="flex h-full items-center gap-2 rounded border border-red-300 bg-red-50 px-2 text-red-600 duration-150 hover:border-red-400"
              >
                {isFullFact ? (
                  <>
                    <Icon className="lucide--archive h-4 w-4" />
                    <span>Archive Fact</span>
                  </>
                ) : (
                  <>
                    <Icon className="lucide--trash-2 h-4 w-4" />
                    <span>Remove Fact from Chronology</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
